import React, { useEffect } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import {
  incomingItemsModalTitleState,
  incomingItemsMonthCustomFilterState,
  incomingItemsMonthFilterState,
  toastBgState,
  toastMessageState,
} from "../../utils/atoms/atoms";
import { useQuery } from "@tanstack/react-query";
import CustomTable from "../common/CustomTable";
import CustomModal from "../CustomModal";
import LoadingComponent from "../../LoadingComponent";
import ToastNotification from "../common/ToastNotification";
import { useForm } from "react-hook-form";
import { getMonthDateString, getMonthFullName } from "../../utils/funcs/time";
import { HasRole } from "../../context/HasRole";
import {
  getChallanDetailsCurrent,
  getChallanDetailsCustom,
  getChallanDetailsPast,
  getPendingSentRecd,
  getRecdStockCurrent,
  getRecdStockCustom,
  getRecdStockPast,
  getSentStockCurrent,
  getSentStockCustom,
  getSentStockPast,
} from "../../api/incoming";
import {
  challanColumns,
  pendingColumns,
  recdColumns,
  sentColumns,
} from "../../utils/table/incomingItemsCols";
import IncomingHistoryModalBody from "./IncomingHistoryModalBody";
import IncomingChallanBulkFormBody from "./IncomingChallanBulkFormBody";
import IncomingItemsFormBody from "../../legacy/IncomingItemsFormBody";
import ChallanDataDownloadModalBody from "./ChallanDataDownloadModalBody";

const IncomingItemsDashboard = () => {
  const [monthFilterType, setMonthFilterType] = useRecoilState(
    incomingItemsMonthFilterState
  );
  const [monthCustomFilter, setMonthCustomFilter] = useRecoilState(
    incomingItemsMonthCustomFilterState
  );
  const { register, watch, reset } = useForm();

  let inStockFn = getSentStockCurrent;
  let outStockFn = getRecdStockCurrent;
  let challanFn = getChallanDetailsCurrent;
  if (monthFilterType === "past") {
    inStockFn = getSentStockPast;
    outStockFn = getRecdStockPast;
    challanFn = getChallanDetailsPast;
  } else if (monthFilterType === "custom") {
    inStockFn = () => getSentStockCustom(monthCustomFilter);
    outStockFn = () => getRecdStockCustom(monthCustomFilter);
    challanFn = () => getChallanDetailsCustom(monthCustomFilter);
  }

  const {
    isLoading: stockInLoading,
    data: sentItems,
    isError: stockInError,
    isSuccess: stockInSuccess,
  } = useQuery({
    queryKey: ["incoming-sent", monthFilterType, monthCustomFilter],
    queryFn: inStockFn,
    enabled: monthFilterType !== "custom" || !!monthCustomFilter,
  });

  const {
    data: recdItems,
    isLoading: stockOutLoading,
    isError: stockOutError,
    isSuccess: stockOutSuccess,
  } = useQuery({
    queryKey: ["incoming-recd", monthFilterType, monthCustomFilter],
    queryFn: outStockFn,
    enabled: monthFilterType !== "custom" || !!monthCustomFilter,
  });

  const {
    data: pendingItems,
    isLoading: pendingLoading,
    isError: pendingError,
    isSuccess: pendingSuccess,
  } = useQuery({
    queryKey: ["incoming-pending"],
    queryFn: getPendingSentRecd,
  });

  const {
    data: challanItems,
    isLoading: challanLoading,
    isError: challanError,
    isSuccess: challanSuccess,
  } = useQuery({
    queryKey: ["incoming-challan", monthFilterType, monthCustomFilter],
    queryFn: challanFn,
    enabled: monthFilterType !== "custom" || !!monthCustomFilter,
  });

  const isLoading =
    stockInLoading || stockOutLoading || pendingLoading || challanLoading;

  const isSuccess =
    stockInSuccess && stockOutSuccess && pendingSuccess && challanSuccess;

  const isError = stockInError || stockOutError || pendingError || challanError;

  const toastBg = useRecoilValue(toastBgState);
  const toastMessage = useRecoilValue(toastMessageState);
  const [currentStatus, setCurrentStatus] = React.useState("PENDING");

  const [incomingItemsModalTitle, setIncomingItemsModalTitle] = useRecoilState(
    incomingItemsModalTitleState
  );

  useEffect(() => {
    const subscription = watch((data) => {
      const customMonth = data["incoming_item_month"];
      if (customMonth) {
        setMonthFilterType("custom");
        setMonthCustomFilter(customMonth);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, setMonthFilterType, setMonthCustomFilter]);

  return (
    <LoadingComponent
      isError={isError}
      isLoading={isLoading}
      isSuccess={isSuccess}
      ComponentBody={
        <React.Fragment>
          <div className="inventory-header d-flex justify-content-between mt-3 mb-3">
            <HasRole roles={["admin", "accounts"]}>
              <div className="d-flex gap-2">
                <div
                  className="styled-btn--std mobile-d-none"
                  data-bs-toggle="modal"
                  data-bs-target="#incomingChallanBulkModal"
                  onClick={() => {
                    setIncomingItemsModalTitle(
                      "Create Challan For New Entries (Comma-separated Values)"
                    );
                  }}
                >
                  Add +
                </div>
                <button
                  type="button"
                  className="styled-btn--std border-0"
                  data-bs-toggle="modal"
                  data-bs-target="#challanDataModal"
                >
                  Challan Data
                </button>
              </div>
            </HasRole>
            <div className="d-flex gap-3">
              <div
                className={`styled-btn--pending-items${
                  currentStatus === "PENDING" ? "--active" : ""
                }`}
                onClick={() => {
                  setCurrentStatus("PENDING");
                }}
              >
                PENDING
              </div>
              <div
                className={`styled-btn--sent${
                  currentStatus === "SENT" ? "--active" : ""
                }`}
                onClick={() => {
                  setCurrentStatus("SENT");
                }}
              >
                SENT
              </div>
              <div
                className={`styled-btn--recd${
                  currentStatus === "RECD" ? "--active" : ""
                }`}
                onClick={() => setCurrentStatus("RECD")}
              >
                RECD
              </div>
              <div
                className={`styled-btn--challan${
                  currentStatus === "CHALLAN" ? "--active" : ""
                }`}
                onClick={() => setCurrentStatus("CHALLAN")}
              >
                CHALLAN
              </div>
            </div>

            <div
              className={`d-flex gap-2 ${
                currentStatus === "PENDING" ? "invisible" : ""
              } `}
            >
              <div
                className={`styled-btn${
                  monthFilterType === "current" || monthFilterType === ""
                    ? "--dark"
                    : "--std"
                }`}
                onClick={() => {
                  setMonthFilterType("current");
                  reset();
                }}
              >
                {getMonthFullName()}
              </div>
              <div
                className={`styled-btn${
                  monthFilterType === "past" ? "--dark" : "--std"
                }`}
                onClick={() => {
                  setMonthFilterType("past");
                  reset();
                }}
              >
                {getMonthFullName(1)}
              </div>
              <form>
                <input
                  type="month"
                  id=""
                  {...register("incoming_item_month")}
                  max={getMonthDateString()}
                />
              </form>
            </div>
          </div>
          {currentStatus === "CHALLAN" ? (
            <CustomTable
              data={challanItems}
              columns={challanColumns}
              isFilter={true}
              isPaginate={true}
              headerBgColor="purple"
            />
          ) : currentStatus === "RECD" ? (
            <CustomTable
              data={recdItems}
              columns={recdColumns}
              isFilter={true}
              isPaginate={true}
              headerBgColor="orange"
            />
          ) : currentStatus === "SENT" ? (
            <CustomTable
              data={sentItems}
              columns={sentColumns}
              isFilter={true}
              isPaginate={true}
              headerBgColor="blue"
            />
          ) : (
            <CustomTable
              data={pendingItems}
              columns={pendingColumns}
              isFilter={true}
              isPaginate={true}
              headerBgColor="green"
            />
          )}

          <CustomModal
            modalId="incomingItemModal"
            modalTitle={incomingItemsModalTitle}
            modalBody={<IncomingItemsFormBody />}
          />
          <CustomModal
            modalId="incomingChallanBulkModal"
            modalTitle={incomingItemsModalTitle}
            modalBody={<IncomingChallanBulkFormBody />}
          />
          <CustomModal
            modalId="challanDataModal"
            modalTitle="Download Challan Data"
            modalBody={<ChallanDataDownloadModalBody />}
          />
          <CustomModal
            modalTitle={incomingItemsModalTitle}
            modalBody={<IncomingHistoryModalBody />}
            modalId="historyIncomingModal"
          />

          <ToastNotification bg={toastBg} message={toastMessage} />
        </React.Fragment>
      }
    />
  );
};

export default IncomingItemsDashboard;
